import { createContext, useContext, useMemo } from "react";
import { useCurrentUser } from "@acto/taskpane/core/data-access/user";
import { User } from "@acto/taskpane/core/domain";
import { belongsToViewer, otherUserName } from "./viewer";

interface Viewer {
  /** The signed-in user, or undefined until the query lands. */
  viewer?: User;
  /** An absent owner or an unresolved viewer counts as the viewer's own, never as a colleague's. */
  isViewer: (owner: User | null | undefined) => boolean;
  /** Names [owner] when they are a colleague; null for the viewer's own or a colleague with no name. */
  colleagueName: (owner: User | null | undefined) => string | null;
}

const ViewerContext = createContext<Viewer>({
  viewer: undefined,
  isViewer: () => true,
  colleagueName: () => null,
});

/**
 * Every consumer of an owner or assignee field compares through this rather than fetching its
 * own copy of the viewer.
 */
export const useViewer = (): Viewer => useContext(ViewerContext);

/** Must be mounted inside the GraphQL provider — it queries the signed-in user. */
export const ViewerProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: viewer } = useCurrentUser();

  // Identity is part of the contract: these must not be rebuilt while the viewer is unchanged.
  const value = useMemo<Viewer>(
    () => ({
      viewer,
      isViewer: (owner) => belongsToViewer(owner, viewer?.id),
      colleagueName: (owner) => otherUserName(owner, viewer?.id),
    }),
    [viewer],
  );

  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
};
